// Tytuł karty przeglądarki — JEDNO miejsce: trasa → nazwa strony, format
// „<strona> · Pogoń Grodzisk Mazowiecki". Strony szczegółów dopisują konkret, gdy go znają
// (`usePageTitle`: mecz, rywal, zawodnik). Trasy /print też tu są — Chromium bierze
// document.title jako tytuł PDF-u, więc wydruki dostają sensowny opis w metadanych.
// Nowa trasa w main.tsx = nowy wpis (pilnuje tego pageTitle.test.ts).

import { createContext, useContext, useEffect, type Dispatch, type SetStateAction } from 'react'
import { useLocation } from 'react-router-dom'

import { matchPattern } from './access'
import { APP_NAME } from './club'

export { APP_NAME }

// Pierwszy pasujący wzorzec wygrywa — węższe przed szerszymi (jak w lib/access.ts).
const TITLES: { pattern: string; title: string }[] = [
  // Raporty meczowe
  { pattern: '/raporty', title: 'Raporty meczowe' },
  { pattern: '/raporty/*', title: 'Raport pomeczowy' },
  // Analiza przeciwnika
  { pattern: '/dossier', title: 'Drużyny' },
  { pattern: '/dossier/*', title: 'Analiza przeciwnika' },
  { pattern: '/gracze', title: 'Zawodnicy ligi' },
  { pattern: '/gracze/*', title: 'Profil zawodnika' },
  // Motoryka, kadra
  { pattern: '/motoryka', title: 'Motoryka' },
  { pattern: '/kadra', title: 'Kadra' },
  // Konta
  { pattern: '/konto/ustaw-haslo', title: 'Ustaw hasło' },
  { pattern: '/konto', title: 'Moje konto' },
  { pattern: '/admin', title: 'Panel admina' },
]

/** Nazwa strony dla ścieżki; null = sama nazwa aplikacji. */
export function routeTitle(pathname: string): string | null {
  return TITLES.find((t) => matchPattern(t.pattern, pathname))?.title ?? null
}

/** „Pogoń Grodzisk Mazowiecki – Wisła Kraków 2:0" — gospodarz pierwszy, wynik w tej samej kolejności. */
export function matchTitle(m: { venue: 'home' | 'away'; team_name: string; opposition_name: string; goals_for: number; goals_against: number }): string {
  return m.venue === 'home'
    ? `${m.team_name} – ${m.opposition_name} ${m.goals_for}:${m.goals_against}`
    : `${m.opposition_name} – ${m.team_name} ${m.goals_against}:${m.goals_for}`
}

/** „Raport pomeczowy · Pogoń Grodzisk Mazowiecki"; bez nazwy strony — sama nazwa. */
export function documentTitle(page: string | null): string {
  return page ? `${page} · ${APP_NAME}` : APP_NAME
}

/** Konkret strony szczegółów — przypięty do ścieżki, żeby nie przeciekł na następną. */
export interface PageTitleOverride { path: string; title: string }

export const PageTitleContext = createContext<Dispatch<SetStateAction<PageTitleOverride | null>>>(() => {})

/**
 * Doprecyzowanie tytułu karty, gdy strona zna już swój obiekt („Pogoń GM – Wisła
 * Kraków 2:0", nazwa rywala, zawodnika). null / pusty = tytuł z mapy tras.
 */
export function usePageTitle(title: string | null | undefined): void {
  const setOverride = useContext(PageTitleContext)
  const { pathname } = useLocation()
  useEffect(() => {
    if (!title) return
    const mine = { path: pathname, title }
    setOverride(mine)
    return () => setOverride((cur) => (cur === mine ? null : cur))
  }, [title, pathname, setOverride])
}
